import { DrawerSection } from '@components/ui/drawer/section';
import { useTranslation } from 'react-i18next';
import { RadioGroup } from '@components/ui/radio';
import { Button } from '@components/ui/button';
import { useSchedulerSidebar } from '@contexts/scheduler/sidebar';
import { useSchedulerContext } from '@contexts/scheduler';
import {
  getEquipmentMembersFromForeman,
  mapEquipmentMembersToSchedulerSidebarCrewMembers,
} from '@contexts/scheduler/sidebar/helpers';
import { SchedulerSidebarConfirmOption } from '../option';
import { useDropdownsContext } from '@contexts/dropdowns';
import { useAccess } from '@hooks/access';

export const SchedulerSidebarConfirmEquipmentAssignment = () => {
  const {
    confirm: {
      equipmentAssignment: { open, keepCurrentEquipment },
    },
    equipmentMembers,
    setState,
  } = useSchedulerSidebar();
  const access = useAccess();

  const {
    active: { schedule },
  } = useSchedulerContext();

  const { fieldUsers } = useDropdownsContext();

  const { t } = useTranslation('app', { keyPrefix: 'schedule.confirm.equipmentAssignment' });
  const { t: tActions } = useTranslation('app', { keyPrefix: 'actions' });

  const handleCancel = () => {
    setState((prev) => ({
      ...prev,
      confirm: {
        ...prev.confirm,
        defaultEquipment: false,
        equipmentAssignment: { ...prev.confirm.equipmentAssignment, open: false },
      },
    }));
  };

  const handleConfirm = () => {
    const defaultEquipmentMembers = getEquipmentMembersFromForeman(
      schedule.foreman?.id,
      fieldUsers,
    );

    setState((prev) => ({
      ...prev,
      defaultEquipment: !prev.confirm.equipmentAssignment.keepCurrentEquipment,
      equipmentMembers: prev.confirm.equipmentAssignment.keepCurrentEquipment
        ? prev.equipmentMembers.filter((el) => el.assetId !== schedule.foreman?.id)
        : mapEquipmentMembersToSchedulerSidebarCrewMembers(defaultEquipmentMembers),
      confirm: {
        ...prev.confirm,
        equipmentAssignment: {
          ...prev.confirm.equipmentAssignment,
          keepCurrentEquipment: true,
          open: false,
        },
      },
    }));
  };

  const currentNames = equipmentMembers
    .filter((el) => el.assetId !== schedule?.foreman?.id)
    .map((el) => el.name);
  const defaultNames = mapEquipmentMembersToSchedulerSidebarCrewMembers(
    getEquipmentMembersFromForeman(schedule?.foreman?.id, fieldUsers),
  ).map((el) => el.name);

  if (!open) return null;

  return (
    <DrawerSection title={t('title')} alwaysOpen>
      <div className="flex flex-col gap-4">
        <RadioGroup
          disabled={access.readOnly}
          value={keepCurrentEquipment ? 'keep' : 'refresh'}
          onValueChange={(v) => {
            setState((prev) => ({
              ...prev,
              confirm: {
                ...prev.confirm,
                equipmentAssignment: {
                  ...prev.confirm.equipmentAssignment,
                  keepCurrentEquipment: v === 'keep',
                },
              },
            }));
          }}
          className="flex flex-col gap-2">
          <SchedulerSidebarConfirmOption
            id="equipment-keep"
            value="keep"
            label={t('keep')}
            items={currentNames}
            emptyLabel={t('empty')}
          />
          <SchedulerSidebarConfirmOption
            id="equipment-refresh"
            value="refresh"
            label={t('refresh', { fieldUser: schedule?.foreman.fullName || '' })}
            items={defaultNames}
            emptyLabel={t('empty')}
          />
        </RadioGroup>
        <div className="flex justify-end gap-2 pt-1">
          <Button variant="ghost" type="button" size="sm" onClick={handleCancel}>
            {tActions('cancel')}
          </Button>
          <Button disabled={access.readOnly} type="button" size="sm" onClick={handleConfirm}>
            {tActions('confirm')}
          </Button>
        </div>
      </div>
    </DrawerSection>
  );
};
